import './_menupage.css';

import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useState } from 'react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { useBrand } from './../brand';
import { LANGS, useLang, useT } from './../i18n';

import trashIcon from './../images/Button_iconButton_delete.svg';
import penIcon from './../images/Button_iconButton_edit.svg';

// 同理要有預設值，否則缺 .env 時公司代號是空的，
// 使用者不知道要填什麼，按「開始」還會被後端擋下來。
const DEFAULT_COMPANY_ID = process.env.REACT_APP_COMPANY_ID || "ormaster";

// 最近一次進入的房間，下次打開首頁直接帶入輸入框。
// localStorage 在隱私模式或被封鎖時會丟例外，讀寫都要包起來。
const LAST_ROOM_KEY = 'ormaster.lastRoom';
const readLastRoom = () => {
  try { return window.localStorage.getItem(LAST_ROOM_KEY) || ''; } catch { return ''; }
};
const saveLastRoom = (room) => {
  try { window.localStorage.setItem(LAST_ROOM_KEY, room); } catch {}
};


// 房間名稱的輸入視窗（新增、修改共用）
//
// 不用原本 Tailwind 的「小螢幕貼底部」排法：一打開就會聚焦輸入框，
// iPhone 直拿時鍵盤彈出正好把貼在底部的視窗整個蓋住，使用者什麼都看不到。
// 改成靠上方顯示，鍵盤只會蓋到下半部。
const RoomDialog = ({ open, title, initialValue, okLabel, onOk, onClose, t }) => {
  const inputRef = useRef(null);
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="room-dialog" initialFocus={inputRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="room-dialog-backdrop" />
        </Transition.Child>
        <div className="room-dialog-wrap">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="room-dialog-panel">
              <Dialog.Title className="room-dialog-title">{title}</Dialog.Title>
              <form
                onSubmit={(e) => { e.preventDefault(); onOk(inputRef.current.value); }}
              >
                <div className="custom-box">
                  <input
                    type="text"
                    placeholder={t('room.namePlaceholder')}
                    ref={inputRef}
                    defaultValue={initialValue}
                    enterKeyHint="done"
                  />
                </div>
                <div className="custom-dialog-button">
                  <button type="button" onClick={onClose}>{t('room.cancel')}</button>
                  <button type="submit">{okLabel}</button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const MainPage = () => {
  const t = useT();
  const [lang, setLang] = useLang();
  const brand = useBrand();
  const cookies = new Cookies();

  const ref = useRef(null);

  const navigate  = useNavigate();
  let roomList = cookies.get('roomList');  
  if( roomList === undefined){
    roomList = [];
  }

  let [listComponent, setListComponent] = useState(roomList);
  const [open, setOpen] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [listID, setListID] = useState(null);
  const [companyId] = useState(DEFAULT_COMPANY_ID);
  // 預設是參與者：進來看的人遠多於主刀醫師，選錯成主持人會拿到踢人、全體靜音的權限
  const [role, setRole] = useState('guest');

  // Jitsi 會把房名正規化成小寫，這裡先統一，避免 JWT 的 room claim 與白板網址對不上。
  const enterRoom = (rawRoom) => {
    const room = String(rawRoom || '').trim().toLowerCase();
    saveLastRoom(room);   // 從清單點進去的也算
    navigate(`/${room}?company=${companyId}&role=${role}`);
  };

  const startMeeting = () => {
    const room = String(ref.current.value || '').trim().toLowerCase();

    if (!room) {
      alert(t('room.nameRequired'));
      return;
    }

    // 初始化 roomList
    if (roomList === undefined || roomList === null) {
      roomList = [];
    }

    // 如果房名不在 cookies 中，才新增
    if (!roomList.includes(room)) {
      roomList.push(room);
      cookies.set('roomList', roomList);
    }

    enterRoom(room);
  };

  // Join Meeting
  const joinMeeting = (index) => {
    enterRoom(listComponent[index]);
  };

  // Add Room
  const addRoom = (value) => {
    const name = String(value || '').trim();
    if (!name) { alert(t('room.nameRequired')); return; }
    const newList = [...listComponent, name];
    setListComponent(newList);
    cookies.set('roomList', newList);
    setOpenNew(false);
  };

  // Delete Meeting 
  const deleteMeeting = (index) => {
    const newList = [...listComponent];
    newList.splice(index, 1);
    setListComponent(newList);
    cookies.set('roomList', newList);
  };

  // Edit Room Name
  // 要複製一份新陣列再改：直接改原陣列再 set 回去，React 認為沒變，畫面不會更新
  const editRoomName = (value) => {
    const name = String(value || '').trim();
    if (!name) { alert(t('room.nameRequired')); return; }
    const newList = [...listComponent];
    // 改的正好是上次進的房間，下次帶入的名稱也要跟著換
    const old = String(newList[listID] || '').trim().toLowerCase();
    if (old && old === readLastRoom()) saveLastRoom(name.toLowerCase());
    newList[listID] = name;
    setListComponent(newList);
    cookies.set('roomList', newList);
    setOpen(false);
  };

  // Toggle Modal
  const openModal = (index) => {
    setListID(index);
    setOpen(true);
  }
  const closeModal = () => {
    setOpen(false);
  }
  const openModalNew = () => {
    setOpenNew(true);
  }
  const closeModalNew = () => {
    setOpenNew(false);
  }

  return (
    <div className="menuPage">
      {/* 有 LOGO：LOGO + 平台名稱；沒有 LOGO：只有平台名稱。
          名稱一律顯示 —— 各院的 LOGO 不一定看得出這是什麼系統。 */}
      <header className={brand.logo ? '' : 'no-logo'}>
        {brand.logo && <img src={brand.logo} alt="" />}
        {brand.appName && <span className="brand-name">{brand.appName}</span>}
        <select
          className="lang-select"
          aria-label={t('menu.language')}
          value={lang}
          onChange={(e) => setLang(e.target.value)}
        >
          {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </header>
      <div className="main-container">
      <div className="main-box">
  {/* 公司代號先不顯示，一律用預設值（REACT_APP_COMPANY_ID，沒設就是 ormaster） */}

  <div className="input-group">
    <input
      type="text"
      id="roomName"
      placeholder={t('menu.roomPlaceholder')}
      ref={ref}
      defaultValue={readLastRoom()}
    />
  </div>

  <div className="input-group">
    <select
      id="role"
      value={role}
      onChange={(e) => setRole(e.target.value)}
    >
      <option value="host">{t('menu.roleHost')}</option>
      <option value="guest">{t('menu.roleGuest')}</option>
    </select>
  </div>

  <div className="start-btn-wrapper">
    <button className="start-btn" onClick={startMeeting}>{t('menu.start')}</button>
  </div>
</div>
      </div>
      <div className="menu-container">
        <div className="menu-box">
          { 
            listComponent.map((list, index) => (
            <div key={index} className="room-list-box">
              <div className="list-box " onClick={() => joinMeeting(index)}>{list}</div>
                <div className="icon-box">
                  <button onClick={() => deleteMeeting(index)}>
                    <img src={trashIcon} alt={t('room.delete')}></img>
                  </button>
                  <button onClick={() => openModal(index)}>
                    <img src={penIcon} alt={t('room.edit')}></img>
                  </button>
                </div>
            </div>
          ))}
        </div>
      </div>
      <button className="add-room collapse" onClick={() => openModalNew()}>{t('menu.add')}</button>

      <RoomDialog
        open={open}
        title={t('room.editTitle')}
        initialValue={listID != null ? listComponent[listID] : ''}
        okLabel={t('room.save')}
        onOk={editRoomName}
        onClose={closeModal}
        t={t}
      />
      <RoomDialog
        open={openNew}
        title={t('room.addTitle')}
        initialValue=""
        okLabel={t('room.add')}
        onOk={addRoom}
        onClose={closeModalNew}
        t={t}
      />
    </div>
  );

}

export default MainPage;


